import React, { useState } from 'react';
import SmsStatusAlert from 'bundles/shared/components/SmsStatusAlert';

const ReportingSidebar = ({ team, activeKey = 'dashboard', onToggleSidebar, variant }) => {
  const teamId = team && team.id;
  const donationsEnabled = !!(team && team.can_access_giving_campaigns);
  const membershipsEnabled = !!(team && team.can_access_memberships);
  const generalLedgerEnabled = !!(team && team.can_access_general_ledger_codes);
  const giftCardsEnabled = !!team?.can_access_gift_card_reports;
  const classesEnabled = !!team?.can_access_classes;
  const staffingEnabled = !!team?.can_access_staffing_reports;
  const diceEnabled = !!team?.can_access_dice_reports;
  const attributionEnabled = !!team?.package?.attribution;

  const menuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      href: teamId ? `/teams/${teamId}/reporting/dashboard` : '#',
      icon: (
        <svg
          className="sprite-icon figma-icon secondary"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <use xlinkHref="#dashboard-grid" />
        </svg>
      ),
    },
    {
      key: 'sales',
      label: 'Sales',
      href: teamId ? `/teams/${teamId}/reporting/dashboard/sales` : '#',
      icon: (
        <svg
          className="sprite-icon figma-icon secondary"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <use xlinkHref="#deals" />
        </svg>
      ),
      children: [
        {
          key: 'sales_tickets',
          label: 'Tickets',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales-tickets` : '#',
        },
        {
          key: 'sales_addons',
          label: 'Add-Ons',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales-addons` : '#',
        },
        {
          key: 'sales_donations',
          label: 'Donations',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales-donations` : '#',
        },
        {
          key: 'sales_memberships',
          label: 'Memberships',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales/memberships` : '#',
        },
        {
          key: 'sales_packs',
          label: 'Packs',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales/packs` : '#',
        },
        {
          key: 'sales_by_source',
          label: 'By Source',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales-by-source` : '#',
        },
        {
          key: 'sales_compare',
          label: 'Compare Events',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales/compare-events` : '#',
        },
        ...(diceEnabled ? [{
          key: 'sales_dice_sync',
          label: 'DICE Sync',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/sales/dice-sync` : '#',
        }] : []),
      ],
    },
    ...(donationsEnabled ? [{
      key: 'donations',
      label: 'Donations',
      href: teamId ? `/teams/${teamId}/reporting/donations` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 17.5L2.94 10.44C1.69 9.19 1.69 7.16 2.94 5.91C4.19 4.66 6.22 4.66 7.47 5.91L10 8.44L12.53 5.91C13.78 4.66 15.81 4.66 17.06 5.91C18.31 7.16 18.31 9.19 17.06 10.44L10 17.5Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'donations_all_donors',
          label: 'All Donors',
          href: teamId ? `/teams/${teamId}/reporting/donations/all-donors` : '#',
        },
        {
          key: 'donations_top_donors',
          label: 'Top Donors',
          href: teamId ? `/teams/${teamId}/reporting/donations/top-donors` : '#',
        },
        {
          key: 'donations_outstanding_pledges',
          label: 'Outstanding Pledges',
          href: teamId ? `/teams/${teamId}/reporting/donations/outstanding-pledges` : '#',
        },
        {
          key: 'donations_giving_by_campaign',
          label: 'Giving by Campaign',
          href: teamId ? `/teams/${teamId}/reporting/donations/giving-by-campaign` : '#',
        },
        {
          key: 'donations_giving_by_fund',
          label: 'Giving by Fund',
          href: teamId ? `/teams/${teamId}/reporting/donations/giving-by-fund` : '#',
        },
        {
          key: 'donations_year_over_year',
          label: 'Year-over-Year Comparison',
          href: teamId ? `/teams/${teamId}/reporting/donations/year-over-year` : '#',
        },
        {
          key: 'donations_lybunt_sybunt',
          label: 'LYBUNT/SYBUNT',
          href: teamId ? `/teams/${teamId}/reporting/donations/lybunt-sybunt` : '#',
        },
        {
          key: 'donations_year_end_summary',
          label: 'Year-End Summaries',
          href: teamId ? `/teams/${teamId}/reporting/donations/year-end-summary` : '#',
        },
        {
          key: 'donations_thank_you',
          label: 'General Thank You',
          href: teamId ? `/teams/${teamId}/reporting/donations/thank-you` : '#',
        },
      ],
    }] : []),
    ...(membershipsEnabled ? [{
      key: 'memberships',
      label: 'Memberships',
      href: teamId ? `/teams/${teamId}/reporting/memberships` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M13.3333 17.5V15.8333C13.3333 14.9493 12.9821 14.1014 12.357 13.4763C11.7319 12.8512 10.8841 12.5 10 12.5H5C4.11594 12.5 3.2681 12.8512 2.64298 13.4763C2.01786 14.1014 1.66667 14.9493 1.66667 15.8333V17.5M18.3333 17.5V15.8333C18.3328 15.0948 18.0869 14.3773 17.6345 13.7936C17.1821 13.2099 16.5487 12.793 15.8333 12.6083M13.3333 2.60833C14.0507 2.79192 14.686 3.20892 15.1397 3.79359C15.5935 4.37827 15.8398 5.09736 15.8398 5.8375C15.8398 6.57764 15.5935 7.29673 15.1397 7.88141C14.686 8.46608 14.0507 8.88308 13.3333 9.06667M10.8333 5.83333C10.8333 7.67428 9.34095 9.16667 7.5 9.16667C5.65905 9.16667 4.16667 7.67428 4.16667 5.83333C4.16667 3.99238 5.65905 2.5 7.5 2.5C9.34095 2.5 10.8333 3.99238 10.8333 5.83333Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'memberships_current_members',
          label: 'Current Members',
          href: teamId ? `/teams/${teamId}/reporting/memberships/current-members` : '#',
        },
        {
          key: 'memberships_pending_cancelations',
          label: 'Pending Cancelations',
          href: teamId ? `/teams/${teamId}/reporting/memberships/pending-cancelations` : '#',
        },
        {
          key: 'memberships_lapsed',
          label: 'Lapsed Members',
          href: teamId ? `/teams/${teamId}/reporting/memberships/lapsed` : '#',
        },
        {
          key: 'memberships_upcoming_renewals',
          label: 'Upcoming Renewals',
          href: teamId ? `/teams/${teamId}/reporting/memberships/upcoming-renewals` : '#',
        },
        {
          key: 'memberships_failed_payments',
          label: 'Failed Payments',
          href: teamId ? `/teams/${teamId}/reporting/memberships/failed-payments` : '#',
        },
        {
          key: 'memberships_revenue',
          label: 'Membership Revenue',
          href: teamId ? `/teams/${teamId}/reporting/memberships/revenue` : '#',
        },
        {
          key: 'memberships_growth',
          label: 'Growth & Retention',
          href: teamId ? `/teams/${teamId}/reporting/memberships/growth` : '#',
        },
        {
          key: 'memberships_engagement',
          label: 'Member Engagement',
          href: teamId ? `/teams/${teamId}/reporting/memberships/engagement` : '#',
        },
      ],
    }] : []),
    ...(giftCardsEnabled ? [{
      key: 'gift_cards',
      label: 'Gift Cards',
      href: teamId ? `/teams/${teamId}/reporting/gift_cards` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M16.6667 10V17.5H3.33333V10M10 17.5V5.83333M10 5.83333H6.25C5.69747 5.83333 5.16756 5.61384 4.77686 5.22314C4.38616 4.83244 4.16667 4.30253 4.16667 3.75C4.16667 3.19747 4.38616 2.66756 4.77686 2.27686C5.16756 1.88616 5.69747 1.66667 6.25 1.66667C9.16667 1.66667 10 5.83333 10 5.83333ZM10 5.83333H13.75C14.3025 5.83333 14.8324 5.61384 15.2231 5.22314C15.6138 4.83244 15.8333 4.30253 15.8333 3.75C15.8333 3.19747 15.6138 2.66756 15.2231 2.27686C14.8324 1.88616 14.3025 1.66667 13.75 1.66667C10.8333 1.66667 10 5.83333 10 5.83333ZM1.66667 5.83333H18.3333V10H1.66667V5.83333Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'gift_cards_liability',
          label: 'Liability',
          href: teamId ? `/teams/${teamId}/reporting/gift_cards/liability` : '#',
        },
        {
          key: 'gift_cards_sales',
          label: 'Sales',
          href: teamId ? `/teams/${teamId}/reporting/gift_cards/sales` : '#',
        },
        {
          key: 'gift_cards_redemptions',
          label: 'Redemptions',
          href: teamId ? `/teams/${teamId}/reporting/gift_cards/redemptions` : '#',
        },
        {
          key: 'gift_cards_ledger',
          label: 'Ledger',
          href: teamId ? `/teams/${teamId}/reporting/gift_cards/ledger` : '#',
        },
      ],
    }] : []),
    ...(classesEnabled ? [{
      key: 'classes',
      label: 'Classes',
      href: teamId ? `/teams/${teamId}/reporting/classes` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M1.66667 7.5L10 3.33333L18.3333 7.5L10 11.6667L1.66667 7.5Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 9.16667V13.3333C5 13.3333 6.66667 15.8333 10 15.8333C13.3333 15.8333 15 13.3333 15 13.3333V9.16667" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'classes_sales',
          label: 'Class Sales',
          href: teamId ? `/teams/${teamId}/reporting/classes/sales` : '#',
        },
        {
          key: 'classes_registrations',
          label: 'Registrations',
          href: teamId ? `/teams/${teamId}/reporting/classes/registrations` : '#',
        },
      ],
    }] : []),
    ...(staffingEnabled ? [{
      key: 'staffing',
      label: 'Staffing',
      href: teamId ? `/teams/${teamId}/reporting/staffing` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M13.3333 17.5V15.8333C13.3333 14.9493 12.9821 14.1014 12.357 13.4763C11.7319 12.8512 10.8841 12.5 10 12.5H5C4.11594 12.5 3.2681 12.8512 2.64298 13.4763C2.01786 14.1014 1.66667 14.9493 1.66667 15.8333V17.5M18.3333 17.5V15.8333C18.3328 15.0948 18.087 14.3773 17.6345 13.7936C17.182 13.2099 16.5484 12.793 15.8333 12.6083M13.3333 2.60833C14.0503 2.79192 14.6859 3.20892 15.1397 3.79359C15.5935 4.37827 15.8398 5.09736 15.8398 5.8375C15.8398 6.57764 15.5935 7.29673 15.1397 7.88141C14.6859 8.46608 14.0503 8.88308 13.3333 9.06667M10.8333 5.83333C10.8333 7.67428 9.34095 9.16667 7.5 9.16667C5.65905 9.16667 4.16667 7.67428 4.16667 5.83333C4.16667 3.99238 5.65905 2.5 7.5 2.5C9.34095 2.5 10.8333 3.99238 10.8333 5.83333Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        { key: 'staffing_labor_by_event', label: 'Labor by event', href: teamId ? `/teams/${teamId}/reporting/staffing/labor_by_event` : '#' },
        { key: 'staffing_labor_by_position', label: 'Labor by position', href: teamId ? `/teams/${teamId}/reporting/staffing/labor_by_position` : '#' },
        { key: 'staffing_hours_by_staff', label: 'Hours by staff member', href: teamId ? `/teams/${teamId}/reporting/staffing/hours_by_staff` : '#' },
        { key: 'staffing_volunteer_hours', label: 'Volunteer hours', href: teamId ? `/teams/${teamId}/reporting/staffing/volunteer_hours` : '#' },
        { key: 'staffing_attendance', label: 'Attendance', href: teamId ? `/teams/${teamId}/reporting/staffing/attendance` : '#' },
      ],
    }] : []),
    {
      key: 'booking',
      label: 'Booking',
      href: teamId ? `/teams/${teamId}/reporting/booking` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M6.66667 9.16667H10M6.66667 12.5H13.3333M15 17.5H5C4.07953 17.5 3.33333 16.7538 3.33333 15.8333V4.16667C3.33333 3.24619 4.07953 2.5 5 2.5H11.1667L16.6667 8V15.8333C16.6667 16.7538 15.9205 17.5 15 17.5Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M11.6667 2.5V7.5H16.6667" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'booking_holds',
          label: 'Holds',
          href: teamId ? `/teams/${teamId}/reporting/booking/holds` : '#',
        },
        {
          key: 'booking_offers',
          label: 'Offers',
          href: teamId ? `/teams/${teamId}/reporting/booking/offers` : '#',
        },
        {
          key: 'booking_contacts',
          label: 'Contacts',
          href: teamId ? `/teams/${teamId}/reporting/booking/contacts` : '#',
        },
        {
          key: 'booking_artist_suggestions',
          label: 'Artist Suggestions',
          href: teamId ? `/teams/${teamId}/reporting/booking/artist_suggestions` : '#',
        },
        {
          key: 'booking_competitive_venues',
          label: 'Competitive Venues',
          href: teamId ? `/teams/${teamId}/reporting/booking/competitive_venues` : '#',
        },
      ],
    },
    {
      key: 'events',
      label: 'Events',
      href: teamId ? `/teams/${teamId}/reporting/dashboard/events` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M17.5 8.33333H2.5M13.3333 1.66667V5M6.66667 1.66667V5M6.5 18.3333H13.5C14.9001 18.3333 15.6002 18.3333 16.135 18.0608C16.6054 17.8212 16.9878 17.4387 17.2275 16.9683C17.5 16.4335 17.5 15.7335 17.5 14.3333V7.33333C17.5 5.93321 17.5 5.23314 17.2275 4.69836C16.9878 4.22796 16.6054 3.84551 16.135 3.60582C15.6002 3.33333 14.9001 3.33333 13.5 3.33333H6.5C5.09987 3.33333 4.3998 3.33333 3.86502 3.60582C3.39462 3.84551 3.01217 4.22796 2.77248 4.69836C2.5 5.23314 2.5 5.93321 2.5 7.33333V14.3333C2.5 15.7335 2.5 16.4335 2.77248 16.9683C3.01217 17.4387 3.39462 17.8212 3.86502 18.0608C4.3998 18.3333 5.09987 18.3333 6.5 18.3333Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'events_attendance',
          label: 'Attendance',
          href: teamId ? `/teams/${teamId}/reporting/dashboard/events/attendance` : '#',
        },
      ],
    },
    {
      key: 'marketing',
      label: 'Marketing',
      href: teamId ? `/teams/${teamId}/reporting/marketing` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M9.16667 3.33333L4.58333 6.66667H2.5C2.04 6.66667 1.66667 7.04 1.66667 7.5V12.5C1.66667 12.96 2.04 13.3333 2.5 13.3333H4.58333L9.16667 16.6667V3.33333Z" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15.3 4.7C16.88 6.28 17.5 8.08 17.5 10C17.5 11.92 16.88 13.72 15.3 15.3M12.65 7.35C13.44 8.14 13.75 9.04 13.75 10C13.75 10.96 13.44 11.86 12.65 12.65" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'marketing_email',
          label: 'Email',
          href: teamId ? `/teams/${teamId}/reporting/marketing-email` : '#',
        },
        {
          key: 'marketing_sms',
          label: 'SMS',
          href: teamId ? `/teams/${teamId}/reporting/marketing-sms` : '#',
        },
        {
          key: 'marketing_web',
          label: 'Web',
          href: teamId ? `/teams/${teamId}/reporting/marketing-web` : '#',
        },
        {
          key: 'marketing_event_health',
          label: 'Event Health',
          href: teamId ? `/teams/${teamId}/reporting/marketing/event-health` : '#',
        },
        {
          key: 'marketing_show_tracker',
          label: 'Show Tracker',
          href: teamId ? `/teams/${teamId}/reporting/marketing/show-tracker` : '#',
        },
        {
          key: 'marketing_promo_codes',
          label: 'Promo Codes',
          href: teamId ? `/teams/${teamId}/reporting/marketing/promo-codes` : '#',
        },
        {
          key: 'marketing_meta_ads',
          label: 'Meta Ads',
          href: teamId ? `/teams/${teamId}/reporting/marketing/meta-ads` : '#',
        },
        {
          key: 'marketing_demographics',
          label: 'Demographics',
          href: teamId ? `/teams/${teamId}/reporting/marketing/demographics` : '#',
        },
        ...(attributionEnabled ? [{
          key: 'marketing_attribution',
          label: 'Attribution',
          href: teamId ? `/teams/${teamId}/reporting/marketing/attribution` : '#',
        }] : []),
      ],
    },
    {
      key: 'finances',
      label: 'Finances',
      href: teamId ? `/teams/${teamId}/reporting/finances` : '#',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 1.66667V18.3333M13.3333 4.16667H8.33333C7.44928 4.16667 6.60143 4.51786 5.97631 5.14298C5.35119 5.7681 5 6.61595 5 7.5C5 8.38405 5.35119 9.2319 5.97631 9.85702C6.60143 10.4821 7.44928 10.8333 8.33333 10.8333H11.6667C12.5507 10.8333 13.3986 11.1845 14.0237 11.8097C14.6488 12.4348 15 13.2826 15 14.1667C15 15.0507 14.6488 15.8986 14.0237 16.5237C13.3986 17.1488 12.5507 17.5 11.6667 17.5H5" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      children: [
        {
          key: 'finances_deposits',
          label: 'Deposits',
          href: teamId ? `/teams/${teamId}/reporting/finances/deposits` : '#',
        },
        {
          key: 'finances_event_pnl',
          label: 'Event P&L',
          href: teamId ? `/teams/${teamId}/reporting/finances/event-pnl` : '#',
        },
        {
          key: 'finances_settlements',
          label: 'Settlements',
          href: teamId ? `/teams/${teamId}/reporting/finances/settlements` : '#',
        },
        {
          key: 'finances_chargebacks',
          label: 'Chargebacks',
          href: teamId ? `/teams/${teamId}/reporting/finances/chargebacks` : '#',
        },
        {
          key: 'finances_line_items',
          label: 'Line Items',
          href: teamId ? `/teams/${teamId}/reporting/finances/line-items` : '#',
        },
        {
          key: 'finances_refunds',
          label: 'Refunds',
          href: teamId ? `/teams/${teamId}/reporting/finances/refunds` : '#',
        },
        {
          key: 'finances_payouts',
          label: 'Payouts',
          href: teamId ? `/teams/${teamId}/reporting/finances/payouts` : '#',
        },
        {
          key: 'finances_payments',
          label: 'Payments & Activities',
          href: teamId ? `/teams/${teamId}/reporting/finances/payments` : '#',
        },
        {
          key: 'finances_sales_tax',
          label: 'Sales Tax',
          href: teamId ? `/teams/${teamId}/reporting/finances/sales-tax` : '#',
        },
        ...(generalLedgerEnabled ? [{
          key: 'finances_general_ledger',
          label: 'General Ledger',
          href: teamId ? `/teams/${teamId}/reporting/finances/general-ledger` : '#',
        }] : []),
        {
          key: 'finances_tax_withholding',
          label: 'Tax Withholding',
          href: teamId ? `/teams/${teamId}/reporting/finances/tax_withholding` : '#',
        },
        {
          key: 'finances_w9_tracking',
          label: 'W-9 Tracking',
          href: teamId ? `/teams/${teamId}/reporting/finances/w9-tracking` : '#',
        },
      ],
    },
    {
      key: 'food_beverage',
      label: 'Food & Beverage',
      href: teamId ? `/teams/${teamId}/reporting/food-and-beverage` : '#',
      children: [
        {
          key: 'food_beverage_overview',
          label: 'Overview',
          href: teamId ? `/teams/${teamId}/reporting/food-and-beverage` : '#',
        },
        {
          key: 'food_beverage_toast_sync',
          label: 'Toast Sync',
          href: teamId ? `/teams/${teamId}/reporting/food-and-beverage/toast-sync` : '#',
        },
        {
          key: 'food_beverage_toast_sales',
          label: 'Toast Sales',
          href: teamId ? `/teams/${teamId}/reporting/food-and-beverage/toast-sales` : '#',
        },
      ],
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M3.33333 2.5V17.5M3.33333 10H7.08333C7.75 10 8.33333 9.41667 8.33333 8.75V3.75C8.33333 3.08333 7.75 2.5 7.08333 2.5H3.33333M15 2.5V7.5C15 9.16667 13.75 10 12.5 10H15V17.5" stroke="#717680" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
  ];

  const isItemActive = (item) => item.key === activeKey;
  const isParentActive = (item) =>
    isItemActive(item) || (item.children && item.children.some(c => c.key === activeKey));

  if (variant === 'mobile') {
    const VISIBLE_COUNT = 3;
    const flatItems = menuItems.flatMap((item) =>
      item.children ? [item, ...item.children] : [item]
    );
    const [startIndex, setStartIndex] = useState(() => {
      const activeIdx = flatItems.findIndex((item) => item.key === activeKey);
      if (activeIdx < 0) return 0;
      return Math.min(activeIdx, Math.max(0, flatItems.length - VISIBLE_COUNT));
    });
    const canGoLeft = startIndex > 0;
    const canGoRight = startIndex + VISIBLE_COUNT < flatItems.length;
    const visibleItems = flatItems.slice(startIndex, startIndex + VISIBLE_COUNT);

    const chevronBtn = (direction, enabled, onClick) => (
      <button
        type="button"
        className="reporting-mobile-tabs-arrow"
        onClick={onClick}
        disabled={!enabled}
        aria-label={direction === 'left' ? 'Previous tabs' : 'Next tabs'}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d={direction === 'left'
              ? 'M10 12L6 8L10 4'
              : 'M6 4L10 8L6 12'}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    );

    return (
      <nav aria-label="Reporting navigation" className="reporting-mobile-tabs">
        {chevronBtn('left', canGoLeft, () => setStartIndex((i) => Math.max(0, i - 1)))}
        <div className="reporting-mobile-tabs-items">
          {visibleItems.map((item) => {
            const active = isItemActive(item);
            return (
              <a
                key={item.key}
                href={item.href}
                className={'reporting-mobile-tab' + (active ? ' reporting-mobile-tab--active' : '')}
              >
                {item.label}
                {item.comingSoon && <span className="reporting-mobile-tab-badge">Soon</span>}
              </a>
            );
          })}
        </div>
        {chevronBtn('right', canGoRight, () => setStartIndex((i) => Math.min(flatItems.length - VISIBLE_COUNT, i + 1)))}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Reporting navigation"
      className="contacts-sidebar-nav h-100 d-flex flex-column"
      style={{ height: '100%' }}
    >
      <div className="contacts-sidebar-header d-flex align-items-center justify-content-between">
        <span>Reports</span>
        {onToggleSidebar && (
          <button
            type="button"
            className="btn btn-sm d-inline-flex align-items-center justify-content-center"
            onClick={onToggleSidebar}
            aria-label="Hide sidebar"
            style={{ padding: '2px 6px', background: 'none', border: 'none' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M13.3332 11.3333H2.6665M2.6665 11.3333L5.33317 14M2.6665 11.3333L5.33317 8.66667M13.3332 4.66667H5.99984M5.99984 4.66667L8.6665 7.33333M5.99984 4.66667L8.6665 2"
                stroke="#A4A7AE"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <ul className="contacts-sidebar-menu list-unstyled mb-3" style={{ marginTop: 16 }}>
        {menuItems.map((item) => (
          <li key={item.key} className="contacts-sidebar-item">
            <a
              href={item.href}
              className={
                'contacts-sidebar-link d-flex align-items-center text-decoration-none' +
                (isItemActive(item) ? ' contacts-sidebar-link--active' : '')
              }
            >
              <span className="d-flex align-items-center">
                <span className="contacts-sidebar-icon d-inline-flex align-items-center justify-content-center">
                  {item.icon}
                </span>
                <span className="contacts-sidebar-label">{item.label}</span>
                {item.comingSoon && (
                  <span className="contacts-sidebar-coming-soon">
                    Coming Soon!
                  </span>
                )}
              </span>
            </a>
            {item.children && isParentActive(item) && (
              <ul className="list-unstyled" style={{ marginLeft: 36, marginTop: 2, marginBottom: 2 }}>
                {item.children.map((child) => (
                  <li key={child.key}>
                    <a
                      href={child.href}
                      className={
                        'contacts-sidebar-link d-flex align-items-center text-decoration-none' +
                        (isItemActive(child) ? ' contacts-sidebar-link--active' : '')
                      }
                      style={{ paddingTop: 4, paddingBottom: 4, fontSize: 14 }}
                    >
                      <span className="contacts-sidebar-label">{child.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {teamId && (
        <div className="mt-auto px-2 pb-3">
          <SmsStatusAlert
            variant="info"
            title="Legacy Reports"
            description="Looking for the original Opendate reports? You can still access them here."
            ctaLabel="View Legacy Reports"
            ctaHref={`/teams/${teamId}/external_reports/looker`}
            ctaTarget="_self"
          />
        </div>
      )}
    </nav>
  );
};

export default ReportingSidebar;
